import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge, Box, Button, Card, CheckboxCard, Dialog, Field, Grid, HStack, Icon, Select, SimpleGrid, Spinner, Stack, Switch, Tabs, Text, Tooltip, createListCollection, useSelectContext } from "@chakra-ui/react";
import { MdAccessTime, MdAssignmentTurnedIn, MdAttachMoney, MdCake, MdEdit, MdGroups, MdHowToReg, MdInfo, MdLocalCafe, MdLocalFireDepartment, MdLogout, MdPhone, MdRecordVoiceOver, MdSports, MdStar } from "react-icons/md";
import { HiCheck, HiX } from "react-icons/hi";
import { CompactEmojiRatingPicker } from "pages/dashboard/Actividades/components/CompactEmojiRatingPicker";
import { labelForRating } from "pages/dashboard/Actividades/components/EmojiRatingBar";
import { TagCombobox } from "components/utils/TagCombobox";
import { DashboardCheckboxCardControl, DashboardSelectContent, DashboardSelectItem, DashboardSelectTrigger } from "components/utils/FormControls";
import { dashboardTabTriggerProps } from "layouts/Dashboard/tabStyles";
import { getAccentHexColor, getAccentTint } from "utils/accentPalette";
import { useDashboardInstructorActivityOverview } from "features/dashboard/instructores/hooks";
import {
  useAddDashboardActividadAsistenciaInstructor,
  useAddDashboardActividadPreceptoria,
  useCreateDashboardActividadEncargo,
  useDashboardActividadAsistenciaInstructores,
  useDashboardActividadEncargos,
  useDashboardActividadPreceptorias,
  useDashboardActividadRosters,
  useDeleteDashboardActividadEncargo,
  usePatchDashboardActividadAsistenciaInstructor,
  usePatchDashboardActividadEncargo,
  useRemoveDashboardActividadAsistenciaInstructor,
  useRemoveDashboardActividadPreceptoria,
} from "features/dashboard/actividades/hooks";
import { formatFechaRange } from "utils/formatters";

const EMPTY = "-";
const DEFAULT_ACTIVITY_COVER_URL =
  "https://images.unsplash.com/photo-1521412644187-c49fa049e84d?auto=format&fit=crop&w=1200&q=60";
const ROLE_OPTIONS = [
  { id: "actividad", label: "Actividad", icon: MdSports, iconColor: "arawaks.skyBlue" },
  { id: "charla", label: "Charla", icon: MdRecordVoiceOver, iconColor: "arawaks.grapePurple" },
  { id: "refrigerio", label: "Refrigerio", icon: MdLocalCafe, iconColor: "arawaks.adventureOrange" },
  { id: "cobranza", label: "Cobranza", icon: MdAttachMoney, iconColor: "arawaks.jungleGreen" },
  { id: "asistencia", label: "Asistencia", icon: MdHowToReg, iconColor: "arawaks.campfireRed" },
];

const PUNTUALIDAD_LABELS = {
  0: "A tiempo",
  1: "Tarde",
  2: "Temprano",
  3: "Falto",
};

const FIXED_ACCENTS = {
  profile: { solid: "arawaks.skyBlue", cssVar: "var(--chakra-colors-arawaks-sky-blue)" },
  role: { solid: "arawaks.grapePurple", cssVar: "var(--chakra-colors-arawaks-grape-purple)" },
  birthday: { solid: "arawaks.rose", cssVar: "var(--chakra-colors-arawaks-rose)" },
  phone: { solid: "arawaks.tropicalTeal", cssVar: "var(--chakra-colors-arawaks-tropical-teal)" },
  generation: { solid: "arawaks.adventureOrange", cssVar: "var(--chakra-colors-arawaks-adventure-orange)" },
  attendance: { solid: "arawaks.jungleGreen", cssVar: "var(--chakra-colors-arawaks-jungle-green)" },
  punctuality: { solid: "arawaks.skyBlue", cssVar: "var(--chakra-colors-arawaks-sky-blue)" },
  rating: { solid: "arawaks.goldenYellow", cssVar: "var(--chakra-colors-arawaks-golden-yellow)" },
  streak: { solid: "arawaks.campfireRed", cssVar: "var(--chakra-colors-arawaks-campfire-red)" },
  performance: { solid: "arawaks.grapePurple", cssVar: "var(--chakra-colors-arawaks-grape-purple)" },
  preceptorias: { solid: "arawaks.tropicalTeal", cssVar: "var(--chakra-colors-arawaks-tropical-teal)" },
  remplazos: { solid: "arawaks.campfireRed", cssVar: "var(--chakra-colors-arawaks-campfire-red)" },
  late: { solid: "arawaks.clayOrange", cssVar: "var(--chakra-colors-arawaks-clay-orange)" },
  early: { solid: "arawaks.playfulIndigo", cssVar: "var(--chakra-colors-arawaks-playful-indigo)" },
  absence: { solid: "arawaks.blueGray", cssVar: "var(--chakra-colors-arawaks-blue-gray)" },
};

const ROLE_ACCENTS = {
  actividad: FIXED_ACCENTS.punctuality,
  charla: FIXED_ACCENTS.performance,
  refrigerio: FIXED_ACCENTS.generation,
  cobranza: FIXED_ACCENTS.attendance,
  asistencia: FIXED_ACCENTS.remplazos,
};

const CHART_ACCENTS = [
  FIXED_ACCENTS.attendance,
  FIXED_ACCENTS.punctuality,
  FIXED_ACCENTS.rating,
  FIXED_ACCENTS.performance,
  FIXED_ACCENTS.preceptorias,
  FIXED_ACCENTS.remplazos,
  FIXED_ACCENTS.late,
  FIXED_ACCENTS.early,
  FIXED_ACCENTS.birthday,
  FIXED_ACCENTS.generation,
];

const TAB_LIST = [
  { value: "info", label: "Info", TabIcon: MdInfo },
  { value: "actividades", label: "Actividades", TabIcon: MdSports },
  { value: "preceptorias", label: "Preceptuados", TabIcon: MdGroups },
  { value: "remplazos", label: "Remplazos", TabIcon: MdAssignmentTurnedIn },
];

function puntualidadToFlags(v) {
  if (v === 3) return { llegoTarde: true, seFueTemprano: true };
  if (v === 1) return { llegoTarde: true, seFueTemprano: false };
  if (v === 2) return { llegoTarde: false, seFueTemprano: true };
  return { llegoTarde: false, seFueTemprano: false };
}

function flagsToPuntualidad(llegoTarde, seFueTemprano) {
  if (llegoTarde && seFueTemprano) return 3;
  if (llegoTarde) return 1;
  if (seFueTemprano) return 2;
  return 0;
}

function SelectValueRole({ placeholder }) {
  const select = useSelectContext();
  const item = (select.selectedItems || [])[0];
  const IconCmp = item?.icon;
  return (
    <Select.ValueText placeholder={placeholder}>
      {item ? (
        <HStack gap="2" w="full" justify="flex-start">
          <Icon color={item?.iconColor || "dashboard.fg.muted"}>{IconCmp ? <IconCmp /> : null}</Icon>
          <Text flex="1" textAlign="left">{item.label}</Text>
        </HStack>
      ) : (
        <Text color="dashboard.fg.muted" textAlign="left">{placeholder}</Text>
      )}
    </Select.ValueText>
  );
}

function RoleSelect({ collection, value, onChange, disabled }) {
  return (
    <Select.Root
      collection={collection}
      size="sm"
      value={value ? [value] : []}
      onValueChange={(e) => onChange(e.value?.[0] ?? "")}
      positioning={{ strategy: "fixed", hideWhenDetached: true }}
      disabled={disabled}
    >
      <Select.HiddenSelect />
      <Select.Control>
        <DashboardSelectTrigger textAlign="left">
          <SelectValueRole placeholder="Sin encargo" />
        </DashboardSelectTrigger>
        <Select.IndicatorGroup>
          <Select.ClearTrigger />
          <Select.Indicator color="dashboard.accent.solid" />
        </Select.IndicatorGroup>
      </Select.Control>
      <Select.Positioner>
        <DashboardSelectContent>
          {collection.items.map((item) => {
            const IconCmp = item.icon;
            return (
              <DashboardSelectItem key={item.id} item={item}>
                <Icon color={item.iconColor || "dashboard.fg.muted"}>{IconCmp ? <IconCmp /> : null}</Icon>
                <Text flex="1" textAlign="left">{item.label}</Text>
                <Select.ItemIndicator />
              </DashboardSelectItem>
            );
          })}
        </DashboardSelectContent>
      </Select.Positioner>
    </Select.Root>
  );
}


function RatingBadge({ value }) {
  if (value === null || value === undefined) return null;
  const palette = Number(value) >= 4 ? "green" : Number(value) >= 3 ? "orange" : "red";
  return (
    <Badge colorPalette={palette} variant="subtle" rounded="full" px="2.5">
      <HStack gap="1">
        <Text>{value}</Text>
        <MdStar />
      </HStack>
    </Badge>
  );
}

function toNullableRating(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isInteger(n)) return null;
  return Math.max(0, Math.min(5, n));
}

function formatPercent(value) {
  return value === null || value === undefined ? EMPTY : `${value}%`;
}

function formatRating(value) {
  return value === null || value === undefined ? EMPTY : Number(value).toFixed(1);
}

function formatStreak(value) {
  const n = Number(value || 0);
  return `${n} ${n === 1 ? "actividad" : "actividades"}`;
}

function normalizeKey(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function accentSolid(tone) {
  return typeof tone === "object" && tone?.solid ? tone.solid : getAccentHexColor(tone);
}

function accentTint(tone, strength = 12) {
  if (typeof tone === "object" && tone?.cssVar) {
    return `color-mix(in srgb, ${tone.cssVar} ${strength}%, transparent)`;
  }
  return getAccentTint(tone, strength);
}

function accentCssVar(tone) {
  return typeof tone === "object" && tone?.cssVar ? tone.cssVar : `var(--chakra-colors-${String(accentSolid(tone)).replace(/\./g, "-")})`;
}

function FactLine({ label, value, icon: IconCmp, tone }) {
  const solid = accentSolid(tone);
  return (
    <HStack gap="2.5" minW="0">
      <Box display="grid" placeItems="center" boxSize="7" rounded="full" bg={accentTint(tone, 14)} color={solid} flexShrink={0}>
        <IconCmp size="15px" />
      </Box>
      <Box minW="0">
        <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textTransform="uppercase" lineClamp={1}>
          {label}
        </Text>
        <Text fontSize="sm" color="dashboard.fg.default" fontWeight="800" lineClamp={1}>
          {value || EMPTY}
        </Text>
      </Box>
    </HStack>
  );
}

function DetailCard({ title, icon: IconCmp, tone, children }) {
  const solid = accentSolid(tone);
  return (
    <Box bg="dashboard.bg.surface" rounded="card" borderWidth="1px" borderColor="dashboard.border.muted" p="5" minW="0">
      <HStack gap="2.5" mb="4">
        <Box display="grid" placeItems="center" boxSize="8" rounded="lg" bg={accentTint(tone, 12)} color={solid} flexShrink={0}>
          <IconCmp size="17px" />
        </Box>
        <Text fontWeight="900" color="dashboard.fg.default" fontSize="sm">
          {title}
        </Text>
      </HStack>
      {children}
    </Box>
  );
}

function DonutSegment({ segment, circumference, offset, radius, strokeWidth, active, hasActive, onHover, onLeave }) {
  if (!segment.value) return null;
  const length = (segment.percent / 100) * circumference;
  return (
    <circle
      cx="80"
      cy="80"
      r={radius}
      fill="none"
      stroke={segment.color}
      strokeWidth={active ? strokeWidth + 4 : strokeWidth}
      strokeDasharray={`${length} ${circumference - length}`}
      strokeDashoffset={-offset}
      strokeLinecap="butt"
      transform="rotate(-90 80 80)"
      cursor="pointer"
      opacity={active || !hasActive ? 1 : 0.36}
      pointerEvents="visibleStroke"
      style={{ transition: "stroke-width 140ms ease, opacity 140ms ease" }}
      onMouseEnter={() => onHover(segment)}
      onMouseLeave={onLeave}
      onFocus={() => onHover(segment)}
      onBlur={onLeave}
      tabIndex={0}
    />
  );
}

function AttendanceDonutChart({ segments, percentage }) {
  const [activeSegment, setActiveSegment] = useState(null);
  const radius = 58;
  const strokeWidth = 18;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  const groups = segments.reduce((acc, segment) => {
    const key = segment.group || segment.label;
    const current = acc.get(key) || {
      label: key,
      value: 0,
      percent: 0,
      color: segment.groupColor || segment.color,
      segments: [],
      isGroup: true,
    };
    current.value += Number(segment.value || 0);
    current.percent += Number(segment.percent || 0);
    current.segments.push(segment);
    acc.set(key, current);
    return acc;
  }, new Map());
  const groupedSegments = Array.from(groups.values());
  const centerTitle = activeSegment?.label || "Asistencia";
  const centerValue = activeSegment ? activeSegment.value : formatPercent(percentage);
  const centerHelper = activeSegment ? `${Math.round(activeSegment.percent)}% del total` : null;
  const isSegmentActive = (segment) =>
    activeSegment?.label === segment.label || (activeSegment?.isGroup && activeSegment.label === segment.group);

  return (
    <Grid templateColumns={{ base: "1fr", md: "220px 1fr" }} gap="5" alignItems="center">
      <Box position="relative" boxSize="220px" mx={{ base: "auto", md: "0" }}>
        <Box as="svg" viewBox="0 0 160 160" w="full" h="full">
          <circle cx="80" cy="80" r={radius} fill="none" stroke="var(--chakra-colors-dashboard-border-muted)" strokeWidth={strokeWidth} />
          {segments.map((segment) => {
            const currentOffset = offset;
            offset += (segment.percent / 100) * circumference;
            return (
              <DonutSegment
                key={segment.label}
                segment={segment}
                circumference={circumference}
                offset={currentOffset}
                radius={radius}
                strokeWidth={strokeWidth}
                active={isSegmentActive(segment)}
                hasActive={!!activeSegment}
                onHover={setActiveSegment}
                onLeave={() => setActiveSegment(null)}
              />
            );
          })}
        </Box>
        <Box position="absolute" inset="0" display="grid" placeItems="center" textAlign="center" pointerEvents="none">
          <Box>
            <Text fontFamily="display" fontSize="4xl" fontWeight="700" lineHeight="1" color="dashboard.fg.default">
              {centerValue}
            </Text>
            <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textTransform="uppercase" mt="1">
              {centerTitle}
            </Text>
            {centerHelper ? (
              <Text fontSize="xs" color="dashboard.fg.subtle" fontWeight="700" mt="1">
                {centerHelper}
              </Text>
            ) : null}
          </Box>
        </Box>
      </Box>

      <Stack gap="3">
        {groupedSegments.map((group) => {
          const groupActive = activeSegment?.label === group.label;
          const groupDimmed = activeSegment && !groupActive && !(activeSegment.group === group.label);
          return (
            <Box key={group.label} minW="0" opacity={groupDimmed ? 0.46 : 1} transition="opacity 140ms ease">
              <HStack
                gap="2.5"
                cursor={group.value > 0 ? "pointer" : "default"}
                rounded="lg"
                px="2"
                py="1.5"
                bg={groupActive ? "dashboard.bg.surfaceMuted" : "transparent"}
                onMouseEnter={() => group.value > 0 && setActiveSegment(group)}
                onMouseLeave={() => setActiveSegment(null)}
                onFocus={() => group.value > 0 && setActiveSegment(group)}
                onBlur={() => setActiveSegment(null)}
                tabIndex={group.value > 0 ? 0 : undefined}
                transition="background 140ms ease"
              >
                <Box boxSize="2.5" rounded="full" bg={group.color} flexShrink={0} />
                <Box minW="0">
                  <Text fontSize="sm" color="dashboard.fg.default" fontWeight="900" lineClamp={1}>
                    {group.label}
                  </Text>
                  <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="700">
                    {group.value} - {Math.round(group.percent)}%
                  </Text>
                </Box>
              </HStack>

              {group.segments.length > 1 ? (
                <Stack gap="1" mt="1" pl="5">
                  {group.segments.map((segment) => {
                    const segmentActive = activeSegment?.label === segment.label;
                    const segmentDimmed = activeSegment && !segmentActive && !groupActive;
                    return (
                      <HStack
                        key={segment.label}
                        gap="2"
                        minW="0"
                        cursor={segment.value > 0 ? "pointer" : "default"}
                        rounded="md"
                        px="2"
                        py="1"
                        bg={segmentActive ? "dashboard.bg.surfaceMuted" : "transparent"}
                        opacity={segmentDimmed ? 0.46 : 1}
                        onMouseEnter={() => segment.value > 0 && setActiveSegment(segment)}
                        onMouseLeave={() => setActiveSegment(null)}
                        onFocus={() => segment.value > 0 && setActiveSegment(segment)}
                        onBlur={() => setActiveSegment(null)}
                        tabIndex={segment.value > 0 ? 0 : undefined}
                        transition="background 140ms ease, opacity 140ms ease"
                      >
                        <Box boxSize="2" rounded="full" bg={segment.color} flexShrink={0} />
                        <Text fontSize="xs" color="dashboard.fg.default" fontWeight="800" lineClamp={1} flex="1">
                          {segment.label}
                        </Text>
                        <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="700" flexShrink={0}>
                          {segment.value}
                        </Text>
                      </HStack>
                    );
                  })}
                </Stack>
              ) : null}
            </Box>
          );
        })}
      </Stack>
    </Grid>
  );
}

function SegmentedBarChart({
  segments,
  totalValue,
  summaryLabel,
  summaryValue,
  summarySecondary,
  formatActiveSecondary,
  emptyText = "Sin datos registrados.",
}) {
  const [activeSegment, setActiveSegment] = useState(null);
  const active = activeSegment || null;
  const total = Number(totalValue || 0);

  if (total <= 0) {
    return <Text fontSize="sm" color="dashboard.fg.muted">{emptyText}</Text>;
  }

  return (
    <Stack gap="4">
      <HStack justify="space-between" align="end" gap="4">
        <Box minW="0">
          <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textTransform="uppercase">
            {active ? active.label : summaryLabel}
          </Text>
          <Text fontFamily="display" fontSize="3xl" color="dashboard.fg.default" fontWeight="700" lineHeight="1" mt="1">
            {active ? active.primary : summaryValue}
          </Text>
        </Box>
        <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textAlign="right" maxW="44">
          {active ? formatActiveSecondary?.(active, total) || active.secondary : summarySecondary || `${total} registros`}
        </Text>
      </HStack>

      <HStack gap="1" h="16" rounded="2xl" overflow="hidden" bg="dashboard.bg.surfaceMuted" p="1">
        {segments.map((segment) => {
          const width = `${Math.max(0, (Number(segment.value || 0) / total) * 100)}%`;
          const isActive = active?.label === segment.label;
          return (
            <Box
              key={segment.label}
              h="full"
              minW={segment.value > 0 ? "8px" : "0"}
              flex={`0 0 ${width}`}
              bg={segment.color}
              opacity={isActive || !active ? 1 : 0.42}
              rounded="xl"
              cursor={segment.value > 0 ? "pointer" : "default"}
              transition="opacity 140ms ease, transform 140ms ease"
              transform={isActive ? "translateY(-2px)" : "none"}
              onMouseEnter={() => segment.value > 0 && setActiveSegment(segment)}
              onMouseLeave={() => setActiveSegment(null)}
              onFocus={() => segment.value > 0 && setActiveSegment(segment)}
              onBlur={() => setActiveSegment(null)}
              tabIndex={segment.value > 0 ? 0 : undefined}
            />
          );
        })}
      </HStack>

      <SimpleGrid columns={{ base: 1, sm: 2 }} gap="3">
        {segments.map((segment) => {
          const isActive = active?.label === segment.label;
          return (
            <HStack
              key={segment.label}
              gap="2.5"
              minW="0"
              cursor={segment.value > 0 ? "pointer" : "default"}
              rounded="lg"
              px="2"
              py="1.5"
              bg={isActive ? "dashboard.bg.surfaceMuted" : "transparent"}
              onMouseEnter={() => segment.value > 0 && setActiveSegment(segment)}
              onMouseLeave={() => setActiveSegment(null)}
              onFocus={() => segment.value > 0 && setActiveSegment(segment)}
              onBlur={() => setActiveSegment(null)}
              tabIndex={segment.value > 0 ? 0 : undefined}
            >
              <Box boxSize="2.5" rounded="full" bg={segment.color} flexShrink={0} />
              <Box minW="0">
                <Text fontSize="sm" color="dashboard.fg.default" fontWeight="800" lineClamp={1}>
                  {segment.label}
                </Text>
                <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="700">
                  {segment.primary} - {Math.round((Number(segment.value || 0) / total) * 100)}%
                </Text>
              </Box>
            </HStack>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
}

function ActivityHeatmap({ activities = [], remplazos = [] }) {
  const [activeItem, setActiveItem] = useState(null);
  const replacementActivityIds = new Set(remplazos.map((row) => String(row.actividadId)).filter(Boolean));
  const sortedActivities = [...activities].sort((a, b) => String(a.fechaInicio || "").localeCompare(String(b.fechaInicio || "")));
  const items = sortedActivities
    .map((activity) => {
      const hasReplacement = replacementActivityIds.has(String(activity.actividadId));
      const flags = puntualidadToFlags(activity.puntualidad);
      const status = hasReplacement
        ? "remplazo"
        : !activity.asistio
          ? "falta"
          : flags.llegoTarde && flags.seFueTemprano
            ? "tardeTemprano"
            : flags.llegoTarde
              ? "tarde"
              : flags.seFueTemprano
                ? "temprano"
                : "asistencia";
      const scoreValue =
        status === "asistencia" || status === "remplazo"
          ? 1
          : status === "tarde" || status === "temprano"
            ? 0.75
            : status === "tardeTemprano"
              ? 0.5
              : 0;
      const tone =
        status === "remplazo"
          ? FIXED_ACCENTS.remplazos
          : status === "asistencia"
            ? FIXED_ACCENTS.attendance
            : status === "tarde"
              ? FIXED_ACCENTS.late
              : status === "temprano"
                ? FIXED_ACCENTS.early
                : status === "tardeTemprano"
                  ? FIXED_ACCENTS.performance
                  : FIXED_ACCENTS.absence;
      return {
        id: activity.actividadId,
        nombre: activity.nombre || "Actividad",
        fechaInicio: activity.fechaInicio,
        fechaFin: activity.fechaFin,
        status,
        scoreValue,
        tone,
        color: accentCssVar(tone),
      };
    });
  const counts = items.reduce(
    (acc, item) => {
      acc[item.status] += 1;
      return acc;
    },
    { asistencia: 0, remplazo: 0, falta: 0, tarde: 0, temprano: 0, tardeTemprano: 0 }
  );
  const baseScore = items.length ? (items.reduce((sum, item) => sum + item.scoreValue, 0) / items.length) * 100 : null;
  const absenceFlags = items.map((item) => item.status === "falta");
  let maxAbsenceStreak = 0;
  let currentAbsenceStreak = 0;
  absenceFlags.forEach((isAbsence) => {
    currentAbsenceStreak = isAbsence ? currentAbsenceStreak + 1 : 0;
    maxAbsenceStreak = Math.max(maxAbsenceStreak, currentAbsenceStreak);
  });
  const recentAbsences = absenceFlags.slice(-3).filter(Boolean).length;
  const streakPenalty = Math.max(0, maxAbsenceStreak - 1) * 8;
  const recencyPenalty = recentAbsences * 5;
  const consistency = baseScore === null ? null : Math.max(0, Math.min(100, Math.round(baseScore - streakPenalty - recencyPenalty)));
  const consistencyLabel =
    consistency === null ? EMPTY : consistency >= 90 ? "Excelente" : consistency >= 75 ? "Buena" : consistency >= 60 ? "Regular" : consistency >= 40 ? "Baja" : "Critica";
  const active = activeItem;

  if (!items.length) {
    return <Text fontSize="sm" color="dashboard.fg.muted">Sin actividades registradas.</Text>;
  }

  return (
    <Stack gap="4">
      <HStack justify="space-between" align="end" gap="4">
        <Box minW="0" flex="1">
          <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textTransform="uppercase" truncate>
            {active ? active.nombre : "Consistencia"}
          </Text>
          <Text fontFamily="display" fontSize="3xl" color="dashboard.fg.default" fontWeight="700" lineHeight="1" mt="1">
            {active
              ? active.status === "asistencia"
                ? "Asistencia"
                : active.status === "remplazo"
                  ? "Remplazo"
                  : active.status === "tarde"
                    ? "Tarde"
                    : active.status === "temprano"
                      ? "Temprano"
                      : active.status === "tardeTemprano"
                        ? "Tarde y temprano"
                        : "Falta"
              : formatPercent(consistency)}
          </Text>
        </Box>
        <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textAlign="right" maxW="48">
          {active ? formatFechaRange({ fechaInicio: active.fechaInicio, fechaFin: active.fechaFin }) : `${consistencyLabel} - ${items.length} actividades`}
        </Text>
      </HStack>

      <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(14px, 1fr))" gap="2" alignItems="center">
        {items.map((item) => {
          const isActive = active?.id === item.id;
          return (
            <Box
              key={item.id}
              aspectRatio="1"
              minW="14px"
              rounded="sm"
              bg={item.color}
              opacity={active && !isActive ? 0.36 : 1}
              outline={isActive ? "2px solid" : "0"}
              outlineColor={item.color}
              outlineOffset="2px"
              cursor="pointer"
              transition="opacity 140ms ease, transform 140ms ease, outline 140ms ease"
              transform={isActive ? "translateY(-2px)" : "none"}
              onMouseEnter={() => setActiveItem(item)}
              onMouseLeave={() => setActiveItem(null)}
              onFocus={() => setActiveItem(item)}
              onBlur={() => setActiveItem(null)}
              tabIndex={0}
            />
          );
        })}
      </Box>

      <HStack gap="3" wrap="wrap">
        {[
          { label: "Asistencias", value: counts.asistencia, tone: FIXED_ACCENTS.attendance },
          { label: "Remplazos", value: counts.remplazo, tone: FIXED_ACCENTS.remplazos },
          { label: "Tarde", value: counts.tarde, tone: FIXED_ACCENTS.late },
          { label: "Temprano", value: counts.temprano, tone: FIXED_ACCENTS.early },
          { label: "Faltas", value: counts.falta, tone: FIXED_ACCENTS.absence },
        ].map((item) => (
          <HStack key={item.label} gap="2">
            <Box boxSize="2.5" rounded="full" bg={accentCssVar(item.tone)} />
            <Text fontSize="xs" color="dashboard.fg.default" fontWeight="800">
              {item.label}
            </Text>
            <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800">
              {item.value}
            </Text>
          </HStack>
        ))}
      </HStack>

    </Stack>
  );
}

function ProfileBand({ instructor, onEdit, asistenciaAverage, asistenciaStreak = 0 }) {
  const clickable = typeof onEdit === "function";
  function handleKeyDown(e) {
    if (!clickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onEdit();
    }
  }

  return (
    <Box
      bg="dashboard.bg.surface"
      rounded="card"
      borderWidth="1px"
      borderColor="dashboard.border.muted"
      p={{ base: "4", md: "5" }}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      cursor={clickable ? "pointer" : "default"}
      onClick={clickable ? onEdit : undefined}
      onKeyDown={handleKeyDown}
      transition="box-shadow 150ms ease, transform 150ms ease, border-color 150ms ease"
      _hover={clickable ? { transform: "translateY(-2px)", boxShadow: "cardLift", borderColor: "dashboard.border.default" } : undefined}
      _focusVisible={{ outline: "2px solid", outlineColor: "dashboard.accent.border", outlineOffset: "2px" }}
    >
      <HStack justify="space-between" align="start" gap="4">
        <Box minW="0">
          <Text fontFamily="display" fontSize={{ base: "2xl", md: "3xl" }} fontWeight="700" color="dashboard.fg.default" lineClamp={1}>
            {instructor?.nombre || "Instructor"}
          </Text>
        </Box>
        {clickable ? (
          <Box display="grid" placeItems="center" boxSize="8" rounded="full" color="dashboard.fg.muted" flexShrink={0}>
            <MdEdit />
          </Box>
        ) : null}
      </HStack>
      <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} gap={{ base: "3", xl: "4" }} mt="4">
        <FactLine label="Calificacion asistencia" value={formatRating(asistenciaAverage)} icon={MdStar} tone={FIXED_ACCENTS.rating} />
        <FactLine label="Nacimiento" value={instructor?.fechaNacimiento} icon={MdCake} tone={FIXED_ACCENTS.birthday} />
        <FactLine label="Telefono" value={instructor?.telefono} icon={MdPhone} tone={FIXED_ACCENTS.punctuality} />
        <FactLine label="Racha asistencia" value={formatStreak(asistenciaStreak)} icon={MdLocalFireDepartment} tone={FIXED_ACCENTS.streak} />
      </SimpleGrid>
    </Box>
  );
}

function InstructorMetrics({ instructor, onEditInfo, resumen = {}, preceptorias = [], remplazos = [], activities = [] }) {
  const remplazosSolicitados = remplazos.filter((row) => row.tipo !== "cubrio").length;
  const attendanceBreakdown = activities.reduce(
    (acc, activity) => {
      if (!activity.asistio) {
        acc.absences += 1;
        return acc;
      }
      const flags = puntualidadToFlags(activity.puntualidad);
      if (flags.llegoTarde && flags.seFueTemprano) acc.lateAndEarly += 1;
      else if (flags.llegoTarde) acc.late += 1;
      else if (flags.seFueTemprano) acc.early += 1;
      else acc.regular += 1;
      return acc;
    },
    { regular: 0, late: 0, early: 0, lateAndEarly: 0, absences: 0 }
  );
  const faltasSinRemplazo = Math.max(0, attendanceBreakdown.absences - remplazos.length);
  const donutRawSegments = [
    {
      group: "Asistencia",
      label: "A tiempo",
      value: attendanceBreakdown.regular,
      tone: FIXED_ACCENTS.attendance,
      groupTone: FIXED_ACCENTS.attendance,
    },
    {
      group: "Asistencia",
      label: "Llegadas tarde",
      value: attendanceBreakdown.late,
      tone: FIXED_ACCENTS.late,
      groupTone: FIXED_ACCENTS.attendance,
    },
    {
      group: "Asistencia",
      label: "Salidas tempranas",
      value: attendanceBreakdown.early,
      tone: FIXED_ACCENTS.early,
      groupTone: FIXED_ACCENTS.attendance,
    },
    {
      group: "Asistencia",
      label: "Tarde y salida temprana",
      value: attendanceBreakdown.lateAndEarly,
      tone: FIXED_ACCENTS.performance,
      groupTone: FIXED_ACCENTS.attendance,
    },
    {
      group: "Remplazos",
      label: "Remplazos",
      value: remplazosSolicitados,
      tone: FIXED_ACCENTS.remplazos,
      groupTone: FIXED_ACCENTS.remplazos,
    },
    {
      group: "Faltas",
      label: "Faltas",
      value: faltasSinRemplazo,
      tone: FIXED_ACCENTS.absence,
      groupTone: FIXED_ACCENTS.absence,
    },
  ];
  const donutTotal = donutRawSegments.reduce((sum, segment) => sum + Number(segment.value || 0), 0);
  const donutSegments = donutRawSegments.map((segment) => ({
    ...segment,
    color: accentCssVar(segment.tone),
    groupColor: accentCssVar(segment.groupTone || segment.tone),
    percent: donutTotal > 0 ? (Number(segment.value || 0) / donutTotal) * 100 : 0,
  }));
  const roleStats = new Map(
    ROLE_OPTIONS.map((role) => [
      role.id,
      {
        id: role.id,
        label: role.label,
        count: 0,
        ratingSum: 0,
        ratingCount: 0,
      },
    ])
  );
  activities.forEach((activity) => {
    (activity.encargos || []).forEach((encargo) => {
      const raw = normalizeKey(encargo.encargo);
      const role = ROLE_OPTIONS.find((option) => normalizeKey(option.id) === raw || normalizeKey(option.label) === raw);
      const roleId = role?.id || raw || "otro";
      const current = roleStats.get(roleId) || {
        id: roleId,
        label: encargo.encargo || "Otro",
        count: 0,
        ratingSum: 0,
        ratingCount: 0,
      };
      current.count += 1;
      const rating = Number(encargo.calificacion);
      if (Number.isFinite(rating)) {
        current.ratingSum += rating;
        current.ratingCount += 1;
      }
      roleStats.set(roleId, current);
    });
  });
  const roleSegments = Array.from(roleStats.values())
    .filter((role) => role.count > 0)
    .map((role, index) => {
      const average = role.ratingCount > 0 ? role.ratingSum / role.ratingCount : null;
      const tone = ROLE_ACCENTS[role.id] || CHART_ACCENTS[index % CHART_ACCENTS.length];
      return {
        label: role.label,
        value: role.count,
        color: accentCssVar(tone),
        primary: formatRating(average),
        secondary: `${role.count} ${role.count === 1 ? "vez" : "veces"} en este encargo`,
      };
    });
  const roleTotal = roleSegments.reduce((sum, segment) => sum + Number(segment.value || 0), 0);
  const preceptoriaGroups = new Map();
  preceptorias.forEach((row) => {
    const key = String(row.socioId ?? row.socioNombre ?? "socio");
    const current = preceptoriaGroups.get(key) || {
      label: row.socioNombre || "Socio",
      count: 0,
    };
    current.count += 1;
    preceptoriaGroups.set(key, current);
  });
  const preceptoriaSegments = Array.from(preceptoriaGroups.values()).map((item, index) => {
    const tone = CHART_ACCENTS[index % CHART_ACCENTS.length];
    return {
      label: item.label,
      value: item.count,
      color: accentCssVar(tone),
      primary: `${item.count} preceptorias`,
      secondary: `${item.count} con este socio`,
    };
  });
  const preceptuadosTotal = preceptoriaGroups.size;
  const asistenciaRatings = activities
    .map((activity) => Number(activity.asistenciaCalificacion))
    .filter((rating) => Number.isFinite(rating));
  const asistenciaAverage =
    asistenciaRatings.length > 0 ? asistenciaRatings.reduce((sum, rating) => sum + rating, 0) / asistenciaRatings.length : null;
  const replacementActivityIds = new Set(remplazos.map((row) => String(row.actividadId ?? "")).filter(Boolean));
  const asistenciaStreak = activities
    .map((activity, index) => ({ activity, index }))
    .sort((a, b) => {
      const aDate = Date.parse(a.activity.fechaInicio || a.activity.fecha || "");
      const bDate = Date.parse(b.activity.fechaInicio || b.activity.fecha || "");
      if (Number.isFinite(aDate) && Number.isFinite(bDate) && aDate !== bDate) return aDate - bDate;
      if (Number.isFinite(aDate) && !Number.isFinite(bDate)) return -1;
      if (!Number.isFinite(aDate) && Number.isFinite(bDate)) return 1;
      return a.index - b.index;
    })
    .reduce(
      (acc, { activity }) => {
        const activityId = String(activity.actividadId ?? activity.id ?? "");
        const countsAsAttendance = !!activity.asistio || (activityId && replacementActivityIds.has(activityId));
        const current = countsAsAttendance ? acc.current + 1 : 0;
        return { current, longest: Math.max(acc.longest, current) };
      },
      { current: 0, longest: 0 }
    ).longest;

  return (
    <Stack gap="4">
      <ProfileBand
        instructor={instructor}
        onEdit={onEditInfo}
        asistenciaAverage={asistenciaAverage}
        asistenciaStreak={asistenciaStreak}
      />

      <Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap="4" alignItems="start">
        <Stack gap="4" minW="0">
          <DetailCard title="Asistencia" icon={MdSports} tone={FIXED_ACCENTS.attendance}>
            <AttendanceDonutChart segments={donutSegments} percentage={resumen.attendancePercentage} />
          </DetailCard>

          <DetailCard title="Consistencia" icon={MdHowToReg} tone={FIXED_ACCENTS.punctuality}>
            <ActivityHeatmap activities={activities} remplazos={remplazos} />
          </DetailCard>
        </Stack>

        <Stack gap="4" minW="0">
          <DetailCard title="Preceptorias" icon={MdGroups} tone={FIXED_ACCENTS.preceptorias}>
            <SegmentedBarChart
              segments={preceptoriaSegments}
              totalValue={resumen.preceptoriasTotal ?? preceptorias.length}
              summaryLabel="Total de preceptorias"
              summaryValue={resumen.preceptoriasTotal ?? preceptorias.length}
              summarySecondary={`${preceptuadosTotal} ${preceptuadosTotal === 1 ? "preceptuado" : "preceptuados"}`}
              formatActiveSecondary={(segment, total) => `${Math.round((Number(segment.value || 0) / total) * 100)}% del total`}
              emptyText="Sin preceptorias registradas."
            />
          </DetailCard>

          <DetailCard title="Desempeño por rol" icon={MdAssignmentTurnedIn} tone={FIXED_ACCENTS.performance}>
            <SegmentedBarChart
              segments={roleSegments}
              totalValue={roleTotal}
              summaryLabel="Desempeño general"
              summaryValue={formatRating(resumen.encargoAverage)}
              emptyText="Sin encargos registrados."
            />
          </DetailCard>
        </Stack>
      </Grid>
    </Stack>
  );
}

function ActivityCard({ activity, onEdit }) {
  const asistio = !!activity.asistio;
  const puntualidadLabel =
    activity.puntualidad === null || activity.puntualidad === undefined
      ? null
      : PUNTUALIDAD_LABELS[activity.puntualidad] || "Registrado";
  const puntualidadPalette = activity.puntualidad === 1 || activity.puntualidad === 3 ? "orange" : "green";

  const openEdit = () => onEdit?.(activity);

  return (
    <Card.Root
      bg={asistio ? "dashboard.bg.surface" : "dashboard.bg.surfaceMuted"}
      borderWidth="1px"
      borderColor={asistio ? "dashboard.border.muted" : "dashboard.border.default"}
      rounded="card"
      opacity={asistio ? 1 : 0.58}
      cursor="pointer"
      overflow="hidden"
      transition="transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease"
      _hover={{ transform: "translateY(-2px)", boxShadow: "cardLift", borderColor: "dashboard.border.default" }}
      role="button"
      tabIndex={0}
      onClick={openEdit}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openEdit();
        }
      }}
    >
      <Box display="flex" minH="150px">
        <Box w={{ base: "128px", md: "180px" }} flexShrink={0} position="relative" overflow="hidden">
          <Box
            as="img"
            src={activity.coverUrl || DEFAULT_ACTIVITY_COVER_URL}
            alt={activity.nombre || "Actividad"}
            w="100%"
            h="100%"
            objectFit="cover"
            filter={asistio ? "none" : "grayscale(1)"}
          />
          <Box position="absolute" inset="0" bg={asistio ? "blackAlpha.100" : "blackAlpha.400"} />
        </Box>

        <Box
          flex="1"
          p={{ base: "3", md: "4" }}
          display="flex"
          flexDirection="column"
          gap="3"
          minW="0"
        >
          <HStack justify="space-between" align="start" gap="3">
            <Box minW="0">
              <Text fontFamily="display" fontSize="lg" fontWeight="700" color="dashboard.fg.default" lineClamp={1}>
                {activity.nombre?.trim() || "Actividad sin nombre"}
              </Text>
              <Text fontSize="xs" color="dashboard.fg.muted">
                {formatFechaRange({ fechaInicio: activity.fechaInicio, fechaFin: activity.fechaFin })}
              </Text>
            </Box>

            {puntualidadLabel ? (
              <Badge colorPalette={puntualidadPalette} variant="subtle" rounded="full" flexShrink={0}>
                {puntualidadLabel}
              </Badge>
            ) : (
              <Badge colorPalette="gray" variant="subtle" rounded="full" flexShrink={0}>
                Sin registro
              </Badge>
            )}
          </HStack>

          <HStack gap="2" wrap="wrap">
            <RatingBadge value={activity.asistenciaCalificacion} />
          </HStack>

          {activity.encargos?.length ? (
            <Box>
              <Text fontSize="xs" color="dashboard.fg.subtle" fontWeight="800" textTransform="uppercase" mb="1">
                Encargos
              </Text>
              <HStack gap="2" wrap="wrap">
                {activity.encargos.map((encargo) => (
                  <Badge key={encargo.id} colorPalette="purple" variant="subtle" rounded="full">
                    <HStack gap="1">
                      <Text>{encargo.encargo || "Encargo"}</Text>
                      {encargo.calificacion != null ? (
                        <>
                          <Text>{encargo.calificacion}</Text>
                          <MdStar />
                        </>
                      ) : null}
                    </HStack>
                  </Badge>
                ))}
              </HStack>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Card.Root>
  );
}

function QuickEditDialog({ open, onOpenChange, activity, instructorId }) {
  const actividadId = activity?.actividadId;
  const rostersQ = useDashboardActividadRosters(open);
  const asistenciaQ = useDashboardActividadAsistenciaInstructores(actividadId, open && !!actividadId);
  const encargosQ = useDashboardActividadEncargos(actividadId, open && !!actividadId);
  const preceptoriasQ = useDashboardActividadPreceptorias(actividadId, open && !!actividadId);

  const addAsistencia = useAddDashboardActividadAsistenciaInstructor();
  const patchAsistencia = usePatchDashboardActividadAsistenciaInstructor();
  const removeAsistencia = useRemoveDashboardActividadAsistenciaInstructor();
  const createEncargo = useCreateDashboardActividadEncargo();
  const patchEncargo = usePatchDashboardActividadEncargo();
  const deleteEncargo = useDeleteDashboardActividadEncargo();
  const addPreceptoria = useAddDashboardActividadPreceptoria();
  const removePreceptoria = useRemoveDashboardActividadPreceptoria();

  const [attended, setAttended] = useState(false);
  const [llegoTarde, setLlegoTarde] = useState(false);
  const [seFueTemprano, setSeFueTemprano] = useState(false);
  const [asistenciaRating, setAsistenciaRating] = useState("");
  const [encargo, setEncargo] = useState("");
  const [desempeno, setDesempeno] = useState("");
  const [socioIds, setSocioIds] = useState([]);
  const [error, setError] = useState("");

  const asistenciaRows = useMemo(() => (Array.isArray(asistenciaQ.data) ? asistenciaQ.data : []), [asistenciaQ.data]);
  const encargosRows = useMemo(() => (Array.isArray(encargosQ.data) ? encargosQ.data : []), [encargosQ.data]);
  const preceptoriaRows = useMemo(() => (Array.isArray(preceptoriasQ.data) ? preceptoriasQ.data : []), [preceptoriasQ.data]);

  const asistenciaRow = useMemo(
    () => asistenciaRows.find((row) => String(row.instructorId) === String(instructorId)),
    [asistenciaRows, instructorId],
  );
  const encargoRow = useMemo(
    () => encargosRows.find((row) => String(row.instructorId) === String(instructorId)),
    [encargosRows, instructorId],
  );
  const preceptoriasForInstructor = useMemo(
    () => preceptoriaRows.filter((row) => String(row.instructorId) === String(instructorId)),
    [preceptoriaRows, instructorId],
  );
  const roleCollection = useMemo(
    () => createListCollection({ items: ROLE_OPTIONS, itemToString: (x) => x.label, itemToValue: (x) => x.id }),
    []
  );

  const socios = useMemo(() => {
    const rows = rostersQ.data?.sociosPeriodo || rostersQ.data?.sociosGrupo || [];
    return Array.isArray(rows) ? rows : [];
  }, [rostersQ.data]);

  const loading = asistenciaQ.isLoading || encargosQ.isLoading || preceptoriasQ.isLoading || rostersQ.isLoading;
  const saving =
    addAsistencia.isPending ||
    patchAsistencia.isPending ||
    removeAsistencia.isPending ||
    createEncargo.isPending ||
    patchEncargo.isPending ||
    deleteEncargo.isPending ||
    addPreceptoria.isPending ||
    removePreceptoria.isPending;

  useEffect(() => {
    if (!open || !activity) return;
    setError("");
    setAttended(!!asistenciaRow);
    const flags = puntualidadToFlags(Number(asistenciaRow?.puntualidad ?? activity.puntualidad ?? 0));
    setLlegoTarde(flags.llegoTarde);
    setSeFueTemprano(flags.seFueTemprano);
    setAsistenciaRating(
      asistenciaRow?.calificacion != null
        ? String(asistenciaRow.calificacion)
        : activity.asistenciaCalificacion != null
          ? String(activity.asistenciaCalificacion)
          : ""
    );
    setEncargo(encargoRow?.encargo || activity.encargos?.[0]?.encargo || "");
    setDesempeno(
      encargoRow?.calificacion != null
        ? String(encargoRow.calificacion)
        : activity.encargos?.[0]?.calificacion != null
          ? String(activity.encargos[0].calificacion)
          : ""
    );
    setSocioIds(preceptoriasForInstructor.map((row) => String(row.socioId)));
  }, [open, activity, asistenciaRow, encargoRow, preceptoriasForInstructor]);

  async function save() {
    if (!actividadId || !instructorId) return;
    setError("");

    try {
      const nextPuntualidad = flagsToPuntualidad(llegoTarde, seFueTemprano);
      const nextAsistenciaRating = toNullableRating(asistenciaRating);

      if (!attended && asistenciaRow) {
        await removeAsistencia.mutateAsync({ actividadId, instructorId });
      }

      if (attended && !asistenciaRow) {
        await addAsistencia.mutateAsync({ actividadId, instructorId, puntualidad: nextPuntualidad });
        if (nextAsistenciaRating != null) {
          await patchAsistencia.mutateAsync({
            actividadId,
            instructorId,
            patch: { calificacion: nextAsistenciaRating },
          });
        }
      }

      if (attended && asistenciaRow) {
        await patchAsistencia.mutateAsync({
          actividadId,
          instructorId,
          patch: { puntualidad: nextPuntualidad, calificacion: nextAsistenciaRating },
        });
      }

      const nextDesempeno = toNullableRating(desempeno);
      if (!encargo && encargoRow) {
        await deleteEncargo.mutateAsync({ actividadId, encargoId: encargoRow.id });
      } else if (encargo && !encargoRow) {
        await createEncargo.mutateAsync({
          actividadId,
          payload: { instructorId, encargo, calificacion: nextDesempeno },
        });
      } else if (encargo && encargoRow) {
        await patchEncargo.mutateAsync({
          actividadId,
          encargoId: encargoRow.id,
          patch: { encargo, calificacion: nextDesempeno },
        });
      }

      const prevSocioIds = new Set(preceptoriasForInstructor.map((row) => String(row.socioId)));
      const nextSocioIds = new Set(socioIds.map(String));
      for (const socioId of prevSocioIds) {
        if (!nextSocioIds.has(socioId)) {
          await removePreceptoria.mutateAsync({ actividadId, instructorId, socioId });
        }
      }
      for (const socioId of nextSocioIds) {
        if (!prevSocioIds.has(socioId)) {
          await addPreceptoria.mutateAsync({ actividadId, instructorId, socioId });
        }
      }

      onOpenChange(false);
    } catch (e) {
      setError(String(e?.message || e));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(e) => onOpenChange(e.open)}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content maxW="2xl" bg="dashboard.bg.dialog" color="dashboard.fg.default" borderWidth="1px" borderColor="dashboard.border.default">
          <Dialog.Header>
            <Dialog.Title>Editar en actividad</Dialog.Title>
          </Dialog.Header>

          <Dialog.Body>
            {loading ? (
              <Box py="8" display="flex" justifyContent="center">
                <Spinner />
              </Box>
            ) : (
              <Stack gap="5">
                <Box>
                  <Text fontFamily="display" fontWeight="700" fontSize="xl">
                    {activity?.nombre || "Actividad"}
                  </Text>
                  <Text color="dashboard.fg.muted" fontSize="sm">
                    {formatFechaRange({ fechaInicio: activity?.fechaInicio, fechaFin: activity?.fechaFin })}
                  </Text>
                </Box>

                <HStack justify="space-between" align="center" bg="dashboard.bg.surfaceMuted" rounded="lg" px="4" py="3">
                  <Box>
                    <Text fontWeight="700">Asistencia</Text>
                    <Text fontSize="sm" color="dashboard.fg.muted">¿El instructor asistió?</Text>
                  </Box>
                  <Switch.Root
                    size="lg"
                    checked={attended}
                    onCheckedChange={(e) => {
                      const next = !!e.checked;
                      setAttended(next);
                      if (!next) {
                        setLlegoTarde(false);
                        setSeFueTemprano(false);
                        setAsistenciaRating("");
                        setEncargo("");
                        setDesempeno("");
                        setSocioIds([]);
                      }
                    }}
                  >
                    <Switch.HiddenInput />
                    <Switch.Control>
                      <Switch.Thumb>
                        <Switch.ThumbIndicator fallback={<HiX color="black" />}>
                          <HiCheck />
                        </Switch.ThumbIndicator>
                      </Switch.Thumb>
                    </Switch.Control>
                  </Switch.Root>
                </HStack>

                {attended && (
                  <>
                    <SimpleGrid columns={2} gap="3" alignItems="end">
                      <Box>
                        <Text fontWeight="700" fontSize="sm" mb="2">Puntualidad</Text>
                        <HStack gap="2">
                          <Tooltip.Root openDelay={0}>
                            <CheckboxCard.Root
                              unstyled
                              checked={llegoTarde}
                              onCheckedChange={(e) => setLlegoTarde(!!e.checked)}
                            >
                              <CheckboxCard.HiddenInput />
                              <Tooltip.Trigger asChild>
                                <DashboardCheckboxCardControl p="2.5" rounded="pill" accentColor="orange.400" boxSize="10">
                                  <Icon color="orange.400" fontSize="xl">
                                    <MdAccessTime />
                                  </Icon>
                                </DashboardCheckboxCardControl>
                              </Tooltip.Trigger>
                            </CheckboxCard.Root>
                            <Tooltip.Positioner>
                              <Tooltip.Content>Llego Tarde</Tooltip.Content>
                            </Tooltip.Positioner>
                          </Tooltip.Root>

                          <Tooltip.Root openDelay={0}>
                            <CheckboxCard.Root
                              unstyled
                              checked={seFueTemprano}
                              onCheckedChange={(e) => setSeFueTemprano(!!e.checked)}
                            >
                              <CheckboxCard.HiddenInput />
                              <Tooltip.Trigger asChild>
                                <DashboardCheckboxCardControl p="2.5" rounded="pill" accentColor="blue.400" boxSize="10">
                                  <Icon color="blue.400" fontSize="xl">
                                    <MdLogout />
                                  </Icon>
                                </DashboardCheckboxCardControl>
                              </Tooltip.Trigger>
                            </CheckboxCard.Root>
                            <Tooltip.Positioner>
                              <Tooltip.Content>Se fue Temprano</Tooltip.Content>
                            </Tooltip.Positioner>
                          </Tooltip.Root>
                        </HStack>
                      </Box>

                      <Box>
                        <Text fontWeight="700" fontSize="sm" mb="2">Evaluación</Text>
                        <HStack gap="2">
                          <CompactEmojiRatingPicker
                            value={asistenciaRating}
                            onChange={(v) => setAsistenciaRating(v)}
                            isDisabled={saving}
                            ariaLabel="Evaluación de asistencia"
                          />
                          {asistenciaRating ? (
                            <Text fontSize="sm" color="dashboard.fg.muted">{labelForRating(Number(asistenciaRating))}</Text>
                          ) : null}
                        </HStack>
                      </Box>
                    </SimpleGrid>

                    <SimpleGrid columns={2} gap="3" alignItems="end">
                      <Field.Root>
                        <Field.Label>Encargo</Field.Label>
                        <RoleSelect collection={roleCollection} value={encargo} onChange={setEncargo} disabled={saving} />
                      </Field.Root>

                      <Box>
                        <Text fontWeight="700" fontSize="sm" mb="2">Desempeño</Text>
                        <HStack gap="2">
                          <CompactEmojiRatingPicker
                            value={desempeno}
                            onChange={(v) => setDesempeno(v)}
                            isDisabled={saving || !encargo}
                            ariaLabel="Desempeño en encargo"
                          />
                          {desempeno && encargo ? (
                            <Text fontSize="sm" color="dashboard.fg.muted">{labelForRating(Number(desempeno))}</Text>
                          ) : null}
                        </HStack>
                      </Box>
                    </SimpleGrid>

                    <TagCombobox
                      label="Preceptorias"
                      placeholder="Buscar socio..."
                      items={socios
                        .map((s) => ({ value: String(s.id || s.socioId || ""), label: s.nombre || s.socioNombre || "Socio" }))
                        .filter((s) => s.value)}
                      value={socioIds}
                      onChange={setSocioIds}
                      showAvatar={false}
                    />
                  </>
                )}

                {error ? (
                  <Text color="dashboard.feedback.error" whiteSpace="pre-wrap">
                    {error}
                  </Text>
                ) : null}
              </Stack>
            )}
          </Dialog.Body>

          <Dialog.Footer>
            <HStack justify="space-between" w="full">
              <Link to={`/dashboard/club/actividades/${actividadId}`}>
                <Button variant="outline" disabled={!actividadId || saving}>
                  Abrir actividad
                </Button>
              </Link>

              <HStack>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button colorPalette="red" onClick={save} disabled={saving || loading}>
                {saving ? <Spinner size="sm" /> : null}
                Guardar
              </Button>
              </HStack>
            </HStack>
          </Dialog.Footer>
          <Dialog.CloseTrigger />
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}

function PreceptoriaCard({ group }) {
  return (
    <Card.Root bg="dashboard.bg.surface" borderWidth="1px" borderColor="dashboard.border.muted" rounded="card">
      <Card.Body gap="2">
        <HStack justify="space-between" align="start">
          <Box minW="0">
            <Text fontWeight="800" color="dashboard.fg.default">
              {group.socioNombre || "Socio"}
            </Text>
            <Text fontSize="sm" color="dashboard.fg.muted">
              {group.actividades.length} {group.actividades.length === 1 ? "actividad" : "actividades"}
            </Text>
          </Box>
          <Badge colorPalette="orange" variant="subtle" rounded="full">
            Preceptoria
          </Badge>
        </HStack>
        <Stack gap="2" mt="2">
          {group.actividades.map((actividad, index) => (
            <Box
              key={`${actividad.actividadId || actividad.actividadNombre || "actividad"}-${index}`}
              bg="dashboard.bg.surfaceMuted"
              rounded="lg"
              px="3"
              py="2"
              borderWidth="1px"
              borderColor="dashboard.border.muted"
            >
              <Text fontSize="sm" fontWeight="800" color="dashboard.fg.default" lineClamp={1}>
                {actividad.actividadNombre || "Actividad"}
              </Text>
              <Text fontSize="xs" color="dashboard.fg.subtle">
                {formatFechaRange({ fechaInicio: actividad.fechaInicio })}
              </Text>
            </Box>
          ))}
        </Stack>
      </Card.Body>
    </Card.Root>
  );
}

function RemplazoCard({ row }) {
  return (
    <Card.Root bg="dashboard.bg.surface" borderWidth="1px" borderColor="dashboard.border.muted" rounded="card">
      <Card.Body gap="2">
        <HStack justify="space-between" align="start">
          <Box>
            <Text fontWeight="800" color="dashboard.fg.default">
              {row.actividadNombre || "Actividad"}
            </Text>
            <Text fontSize="sm" color="dashboard.fg.muted">
              {`Pidio remplazo a ${row.remplazoNombre || EMPTY}`}
            </Text>
          </Box>
          <Badge colorPalette="blue" variant="subtle" rounded="full">
            Solicito
          </Badge>
        </HStack>
        <HStack gap="2" wrap="wrap">
          <Text fontSize="xs" color="dashboard.fg.subtle">
            {formatFechaRange({ fechaInicio: row.fechaInicio })}
          </Text>
          <RatingBadge value={row.calificacion} />
        </HStack>
      </Card.Body>
    </Card.Root>
  );
}

const InstructorDetailsTabs = ({ sidebarHeader = null, instructorInfo = null, onEditInfo }) => {
  const { id } = useParams();
  const [view, setView] = useState("info");
  const [editingActivity, setEditingActivity] = useState(null);
  const q = useDashboardInstructorActivityOverview(id, !!id);
  const data = q.data || {};

  const activities = useMemo(() => (Array.isArray(data.actividades) ? data.actividades : []), [data.actividades]);
  const preceptorias = useMemo(() => (Array.isArray(data.preceptorias) ? data.preceptorias : []), [data.preceptorias]);
  const remplazos = useMemo(() => (Array.isArray(data.remplazos) ? data.remplazos : []), [data.remplazos]);
  const remplazosSolicitados = useMemo(() => remplazos.filter((row) => row.tipo !== "cubrio"), [remplazos]);
  const preceptoriasPorSocio = useMemo(() => {
    const groups = new Map();
    preceptorias.forEach((row) => {
      const key = String(row.socioId ?? row.socioNombre ?? "socio");
      const current = groups.get(key) || {
        socioId: row.socioId,
        socioNombre: row.socioNombre,
        actividades: [],
      };
      current.actividades.push({
        actividadId: row.actividadId,
        actividadNombre: row.actividadNombre,
        fechaInicio: row.fechaInicio,
      });
      groups.set(key, current);
    });
    return Array.from(groups.values()).map((group) => ({
      ...group,
      actividades: group.actividades.sort((a, b) => String(a.fechaInicio || "").localeCompare(String(b.fechaInicio || ""))),
    }));
  }, [preceptorias]);

  if (q.isLoading) {
    return (
      <Box bg="dashboard.bg.surface" rounded="card" p="8" display="flex" justifyContent="center">
        <Spinner />
      </Box>
    );
  }

  if (q.isError) {
    return (
      <Box bg="dashboard.bg.surface" rounded="card" p="6">
        <Text color="dashboard.feedback.error" fontWeight="bold">No se pudo cargar el historial.</Text>
        <Text as="pre" whiteSpace="pre-wrap" color="dashboard.feedback.error" mt="2">
          {String(q.error?.message || q.error)}
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Tabs.Root
        value={view}
        onValueChange={(e) => setView(e.value)}
        orientation="vertical"
        variant="subtle"
        colorPalette="red"
        w="full"
        display="block"
      >
        <Grid templateColumns={{ base: "1fr", md: "270px 1fr" }} gap="5" alignItems="start">
          <Box
            bg="dashboard.bg.surface"
            rounded="card"
            borderWidth="1px"
            borderColor="dashboard.border.muted"
            overflow="hidden"
            position={{ md: "sticky" }}
            top={{ md: "4" }}
          >
            {sidebarHeader}
            <Tabs.List p="2" gap="0.5" flexDirection="column" bg="transparent" w="full">
              {TAB_LIST.map(({ value, label, TabIcon }) => {
                const isSelected = view === value;
                return (
                  <Tabs.Trigger key={value} value={value} w="full" {...dashboardTabTriggerProps}>
                    <HStack w="full" gap="2.5">
                      <Box
                        display="grid"
                        placeItems="center"
                        boxSize="6"
                        rounded="md"
                        flexShrink={0}
                        bg={isSelected ? "red.100" : "transparent"}
                        _dark={{ bg: isSelected ? "red.800" : "transparent" }}
                        transition="background 150ms ease"
                      >
                        <TabIcon size="15px" />
                      </Box>
                      <Text>{label}</Text>
                    </HStack>
                  </Tabs.Trigger>
                );
              })}
              <Tabs.Indicator rounded="md" />
            </Tabs.List>
          </Box>

          <Box minW="0">
            <Tabs.Content value="info" p="0">
              <InstructorMetrics
                instructor={instructorInfo}
                onEditInfo={onEditInfo}
                resumen={data.resumen}
                preceptorias={preceptorias}
                remplazos={remplazosSolicitados}
                activities={activities}
              />
            </Tabs.Content>

            <Tabs.Content value="actividades" p="0">
              <SimpleGrid columns={{ base: 1, xl: 2 }} gap="4">
                {activities.map((activity) => (
                  <ActivityCard key={activity.actividadId} activity={activity} onEdit={setEditingActivity} />
                ))}
              </SimpleGrid>
            </Tabs.Content>

            <Tabs.Content value="preceptorias" p="0">
              {preceptoriasPorSocio.length ? (
                <SimpleGrid columns={{ base: 1, xl: 2 }} gap="4">
                  {preceptoriasPorSocio.map((group) => (
                    <PreceptoriaCard key={group.socioId ?? group.socioNombre} group={group} />
                  ))}
                </SimpleGrid>
              ) : (
                <Box bg="dashboard.bg.surface" rounded="card" borderWidth="1px" borderColor="dashboard.border.muted" p="6">
                  <Text color="dashboard.fg.muted">Sin preceptorias registradas.</Text>
                </Box>
              )}
            </Tabs.Content>

            <Tabs.Content value="remplazos" p="0">
              {remplazosSolicitados.length ? (
                <SimpleGrid columns={{ base: 1, xl: 2 }} gap="4">
                  {remplazosSolicitados.map((row) => (
                    <RemplazoCard key={row.id} row={row} />
                  ))}
                </SimpleGrid>
              ) : (
                <Box bg="dashboard.bg.surface" rounded="card" borderWidth="1px" borderColor="dashboard.border.muted" p="6">
                  <Text color="dashboard.fg.muted">Sin remplazos registrados.</Text>
                </Box>
              )}
            </Tabs.Content>
          </Box>
        </Grid>
      </Tabs.Root>

      <QuickEditDialog
        open={!!editingActivity}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setEditingActivity(null);
        }}
        activity={editingActivity}
        instructorId={id}
      />
    </Box>
  );
};

export default InstructorDetailsTabs;
